import {Replay} from "@mui/icons-material";
import {Alert, Button, IconButton} from "@mui/material";
import FormControl from "@mui/material/FormControl";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Typography from "@mui/material/Typography";
import {useNavigate} from "@tanstack/react-router";
import {type ReactElement, useEffect} from "react";
import {useFormContext, useWatch} from "react-hook-form";
import {buildPayAmount} from "@/components/Forms/CartPaymentForm.tsx";
import type {PaymentFormSchemaType} from "@/components/Forms/PaymentForm.tsx";
import {useCartContext} from "@/components/Providers/CartProvider.tsx";
import {useFeeProvider} from "@/components/Providers/FeesProvider.tsx";
import {apiEndpoint, useUser} from "@/components/Providers/JWTProvider.tsx";
import {
    CONNECTED,
    CONNECTING,
    type CompleteSetupintent,
    filemakerLogTerminalEvent,
    NOT_READY,
    type PaymentStatus,
    PROCESSING,
    READY,
    useStripeTerminal,
    WAITING_FOR_INPUT,
} from "@/components/Providers/StripeTerminalProvider.tsx";
import useFMCartPerson from "@/hooks/useFMCartPerson.ts";
import type {CreateSetupIntentResponse} from "@/types/CreateSetupIntentResponse.ts";
import type {SetupIntent} from "@/types/SetupIntent.tsx";
import {closeFMWindow} from "@/utils/fmScripts.ts";

type Props = {
    setupIntent: SetupIntent;
    cost: number;
    courseSectionId: string | null;
    stripeErrorMessage: string;
    setStripeErrorMessage: (stripeErrorMessage: string) => void;
    cartHasFees?: boolean;
    setIsProcessing: React.Dispatch<React.SetStateAction<boolean>>;
    isProcessing: boolean;
};

type TerminalPaymentIntent = {
    paymentIntent: string;
    clientSecret: string;
};

const mapPaymentStatus = (
    status: PaymentStatus | undefined,
    capturedPayment: CompleteSetupintent | null,
): string | undefined => {
    if (capturedPayment?.paymentIntentId) {
        return "Ready to pay now";
    }
    switch (status) {
        case WAITING_FOR_INPUT:
            return "Tap or Insert card";
        case READY:
            return "Ready For Card";
        case NOT_READY:
            return "Reader Not Ready for card";
        case PROCESSING:
            return "Processing card";
    }
    return status;
};

//https://docs.stripe.com/terminal/quickstart?lang=node
export const StripeTerminal = ({
    cost,
    setStripeErrorMessage,
    stripeErrorMessage,
    cartHasFees,
    setIsProcessing,
    isProcessing,
}: Props): ReactElement => {
    const {user, isEmployee} = useUser();
    const navigate = useNavigate();
    const {cart, cartPaymentSuccess, refreshCart} = useCartContext();
    const {refreshPurchaseHistory, refreshFees} = useFeeProvider();
    const {
        terminal,
        connected,
        paymentStatus,
        connectReader,
        reader,
        readers,
        capturedPayment,
        setCapturedPayment,
    } = useStripeTerminal();
    const form = useFormContext<PaymentFormSchemaType>();
    const paymentType = useWatch<PaymentFormSchemaType>({name: "paymentType"});
    const partialPaymentAmount = useWatch<PaymentFormSchemaType>({name: "partialPaymentAmount"});
    const fmCartPerson = useFMCartPerson();

    const collectPayment = async (): Promise<undefined | CompleteSetupintent> => {
        setCapturedPayment(null);
        setStripeErrorMessage("");
        if (!(user && cart && terminal)) {
            setStripeErrorMessage("Error collecting payment.");
            return;
        }

        const formValidation = await form.trigger();
        if (!formValidation) {
            setStripeErrorMessage("Fix form Error and try terminal again.");
            return;
        }
        const payNowAmount = buildPayAmount(
            paymentType?.toString(),
            cost,
            partialPaymentAmount?.toString(),
        );

        if (paymentStatus === WAITING_FOR_INPUT) {
            await terminal.clearReaderDisplay();
            setStripeErrorMessage("");
        }

        filemakerLogTerminalEvent({
            data: {
                reader: reader?.id ?? "unknown",
            },
            errorCode: 0,
            description: "StartingCollectPayment",
        });
        const scriptResult = await fmCartPerson();
        if (scriptResult) {
            setStripeErrorMessage(scriptResult);
            setIsProcessing(false);
            return;
        }

        const paymentIntentRequest = await user.apiFetch(
            new URL("/v1/payment/create-terminal-payment-intent", apiEndpoint).toString(),
            {
                method: "POST",
                body: JSON.stringify({
                    cartId: cart.id,
                    payNowAmount,
                }),
            },
        );
        if (!paymentIntentRequest.ok) {
            const failure = (await paymentIntentRequest.json().catch(() => null)) as {
                hint?: string;
            } | null;
            setStripeErrorMessage(
                failure?.hint ?? "Could not start the payment. Please try again.",
            );
            setIsProcessing(false);
            return;
        }

        const terminalPaymentIntent = (await paymentIntentRequest.json()) as TerminalPaymentIntent;
        if (terminal.getPaymentStatus() === WAITING_FOR_INPUT) {
            try {
                await terminal.cancelCollectPaymentMethod();
            } catch (error) {
                console.error("error canceling other payment", error);
            }
        }

        filemakerLogTerminalEvent({
            data: {
                reader: reader?.id ?? "unknown",
                paymentIntent: terminalPaymentIntent.paymentIntent,
            },
            errorCode: 0,
            description: "StartingCollectPayment",
        });
        const response = await terminal.collectPaymentMethod(terminalPaymentIntent.clientSecret, {
            config_override: {
                request_dynamic_currency_conversion: false,
            },
        });

        if ("error" in response) {
            filemakerLogTerminalEvent({
                data: {
                    reader: reader?.id ?? "unknown",
                    paymentIntent: terminalPaymentIntent.paymentIntent,
                    stripeMessage: response.error.message,
                    stripeCode: response.error.code,
                },
                errorCode: 2,
                description: "ErrorCollectPayment",
            });
            if (
                response.error.code === "canceled" ||
                response.error.message === "Transaction is cancelled by the user."
            ) {
                return;
            }
            console.error("Error collecting payment", response.error);
            setStripeErrorMessage(
                `There was an error collecting the card: ${response.error.message}`,
            );
        } else {
            const processedPayment = await terminal.processPayment(response.paymentIntent);
            if ("error" in processedPayment) {
                filemakerLogTerminalEvent({
                    data: {
                        reader: reader?.id ?? "unknown",
                        paymentIntent: terminalPaymentIntent.paymentIntent,
                        stripeMessage: processedPayment.error.message,
                        stripeCode: processedPayment.error.code,
                    },
                    errorCode: 2,
                    description: "ErrorProcessPayment",
                });
                setStripeErrorMessage(
                    `There was an error collecting the card: ${processedPayment.error.message}`,
                );
            } else {
                const capturedPayment = {
                    setupId: null,
                    paymentIntentId: processedPayment.paymentIntent.id,
                    cartId: cart.id,
                    paymentMethod: "card",
                    courseSectionId: null,
                    payNowAmount,
                    paymentType: "stripe",
                    terminalId: reader?.id,
                } satisfies CompleteSetupintent;
                setCapturedPayment(capturedPayment);
                return capturedPayment;
            }
            setIsProcessing(false);
        }
    };

    const processCard = async () => {
        if (!user) {
            setStripeErrorMessage("User is not ready.  Please reload page.");
            setIsProcessing(false);
            return;
        }

        const capture: CompleteSetupintent | null | undefined = capturedPayment;
        if (!capture) {
            setStripeErrorMessage("Reader is not ready to proccess card");
            setIsProcessing(false);
            return;
        }
        const url = new URL(
            "/v1/payment/complete-setup-intent",
            import.meta.env.VITE_APP_API_ENDPOINT,
        );

        const response = await user.apiFetch(url.toString(), {
            method: "POST",
            body: JSON.stringify(capture),
        });

        const json = (await response.json()) as CreateSetupIntentResponse;

        if (json.errorCode !== 0) {
            setStripeErrorMessage(json.message);
            return;
        }

        const cartId = cart?.id;

        await refreshCart();
        await refreshPurchaseHistory();
        await refreshFees();

        closeFMWindow();

        await cartPaymentSuccess(`Payment of $${capture.payNowAmount} completed.`);
        setIsProcessing(false);
        const route: string =
            user && "userType" in user && user.userType === "guest_user"
                ? `/cart-receipt/${cartId}`
                : isEmployee
                  ? "/instructor/fees"
                  : cartHasFees
                    ? "/fees"
                    : "/my-registrations";
        await navigate({to: route, search: {purchased: "purchased"}});
    };

    useEffect(() => {
        if (!isProcessing) {
            setStripeErrorMessage("Card is not ready to process");
            setIsProcessing(false);
            return;
        }
        processCard()
            .catch(error => {
                if (error instanceof Error) {
                    setStripeErrorMessage(`Error processing card: ${error.message}`);
                } else {
                    setStripeErrorMessage("Error processing card");
                }
            })
            .finally(() => {
                setIsProcessing(false);
            });
    }, [isProcessing]);

    useEffect(() => {
        setCapturedPayment(null);
    }, [stripeErrorMessage]);

    useEffect(() => {
        const effect = async () => {
            setCapturedPayment(null);
            setIsProcessing(false);
            if (!(reader && terminal) || connected != CONNECTED) {
                return;
            }
            await collectPayment();
        };

        effect().catch(err => {
            // if we disconnect while waiting for a payment intent we likely don't want to display an error
            if (terminal?.getConnectedReader() === reader) {
                setStripeErrorMessage("There was an error collecting the card.");
                console.error("There was an error collecting the card.", err);
            }
            setIsProcessing(false);
        });
    }, [reader, cost, connected, partialPaymentAmount, paymentType]);

    useEffect(() => {
        setIsProcessing(false);
    }, [connected]);

    return (
        <Grid container spacing={2}>
            <Grid
                size={{
                    xs: 12,
                    md: 10,
                }}
            >
                <Typography>Select Reader</Typography>
                <FormControl fullWidth>
                    <Select
                        label="Select Reader"
                        displayEmpty={true}
                        id="select-card-reader"
                        value={reader?.id ?? ""}
                        onChange={event => {
                            terminal?.disconnectReader();
                            const reader = readers.find(r => r.id === event.target.value);
                            if (reader && connectReader) {
                                connectReader(reader).catch(e => {
                                    if (e && "message" in e && typeof e.message === "string") {
                                        setStripeErrorMessage(e.message);
                                    } else {
                                        setStripeErrorMessage("Error connecting to stripe reader");
                                    }
                                });
                            } else if (connectReader) {
                                connectReader(undefined).catch(e => {
                                    if (e && "message" in e && typeof e.message === "string") {
                                        setStripeErrorMessage(e.message);
                                    } else {
                                        setStripeErrorMessage("Error connecting to stripe reader");
                                    }
                                });
                            }
                        }}
                    >
                        <MenuItem value={""}>-- Select Reader --</MenuItem>
                        {readers.map(r => (
                            <MenuItem key={`reader-${r.id}`} value={r.id}>
                                {r.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>
            <Grid
                size={{
                    xs: 12,
                    md: 2,
                }}
                sx={{
                    alignContent: "flex-end",
                }}
            >
                {terminal !== undefined &&
                    reader !== undefined &&
                    connectReader !== undefined &&
                    !isProcessing && (
                        <IconButton
                            aria-label="Reconnect to Terminal"
                            color="primary"
                            title="Reconnect to Terminal"
                            onClick={() => {
                                terminal?.disconnectReader().then(async () => {
                                    await connectReader(reader);
                                });
                            }}
                        >
                            <Replay fontSize={"large"} />
                        </IconButton>
                    )}
            </Grid>
            <Grid size={12}>
                <Alert
                    severity={
                        connected === CONNECTED
                            ? "success"
                            : connected === CONNECTING
                              ? "warning"
                              : "error"
                    }
                >
                    Card Reader Status: {reader?.label} {connected}
                </Alert>
            </Grid>
            {connected === CONNECTED && (
                <Grid size={12}>
                    <Alert
                        severity={
                            capturedPayment?.paymentMethod !== undefined ? "success" : "warning"
                        }
                    >
                        Payment status: {mapPaymentStatus(paymentStatus, capturedPayment)}
                    </Alert>
                </Grid>
            )}
            <Grid size={12}>
                {stripeErrorMessage !== "" && <Alert severity="error">{stripeErrorMessage}</Alert>}
                {(capturedPayment !== null || stripeErrorMessage !== "") && !isProcessing && (
                    <Button
                        sx={{
                            backgroundColor: "primary",
                            color: "#fff",
                            pl: 2,
                            pr: 2,
                            borderRadius: "8px",
                            fontWeight: 800,
                            mt: 2,
                        }}
                        variant="contained"
                        onClick={() => {
                            collectPayment().catch(e => {
                                console.error("error retrying payment", e);
                            });
                        }}
                    >
                        Re-Capture payment
                    </Button>
                )}
            </Grid>
        </Grid>
    );
};
